import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "../Settings.css";
import "../../styles/Tabs.css";
import "./Carriers.css";

const Carriers = () => {
  const [carriers, setCarriers] = useState([]);
  const [name, setName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedCarrier, setSelectedCarrier] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  const filteredCarriers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return carriers;

    return carriers.filter((carrier) =>
      String(carrier.name || "").toLowerCase().includes(term)
    );
  }, [carriers, searchTerm]);

  useEffect(() => {
    loadCarriers();
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const sortCarriers = (list) => {
    return [...list].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  };

  const loadCarriers = async () => {
    try {
      setLoading(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/carriers`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load carriers.");
      }

      setCarriers(sortCarriers(Array.isArray(data) ? data : []));
    } catch (err) {
      console.error("Error fetching carriers:", err);
      setError(err.message || "Failed to load carriers.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const names = name
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!names.length) {
      setError("Please enter at least one carrier name.");
      setSuccess("");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const addedCarriers = [];
      let skippedCount = 0;

      for (const carrierName of names) {
        const res = await fetch(`${API_BASE_URL}/api/carriers`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ name: carrierName })
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (String(data.error || "").toLowerCase().includes("already exists")) {
            skippedCount += 1;
            continue;
          }

          throw new Error(data.error || data.message || `Failed to add ${carrierName}.`);
        }

        addedCarriers.push({
          id: data.id,
          name: data.name || carrierName
        });
      }

      setCarriers((prev) => sortCarriers([...addedCarriers, ...prev]));
      setName("");

      const addedMessage = `${addedCarriers.length} carrier(s) added successfully.`;
      const skippedMessage = skippedCount ? ` ${skippedCount} duplicate carrier(s) skipped.` : "";

      setSuccess(`${addedMessage}${skippedMessage}`);
    } catch (err) {
      console.error("Error adding carriers:", err);
      setError(err.message || "Failed to add carrier(s).");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCarrier) return;

    const cleanName = editedName.trim();

    if (!cleanName) {
      setError("Carrier name is required.");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/carriers/${selectedCarrier.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: cleanName })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to update carrier.");
      }

      setCarriers((prev) =>
        sortCarriers(
          prev.map((carrier) =>
            carrier.id === selectedCarrier.id ? { ...carrier, name: cleanName } : carrier
          )
        )
      );

      setSelectedCarrier(null);
      setEditedName("");
      setSuccess("Carrier updated successfully.");
    } catch (err) {
      console.error("Carrier update error:", err);
      setError(err.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCarrier) return;

    if (!window.confirm(`Delete ${selectedCarrier.name}?`)) return;

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/carriers/${selectedCarrier.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to delete carrier.");
      }

      setCarriers((prev) => prev.filter((carrier) => carrier.id !== selectedCarrier.id));
      setSelectedCarrier(null);
      setEditedName("");
      setSuccess("Carrier deleted successfully.");
    } catch (err) {
      console.error("Carrier delete error:", err);
      setError(err.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page settings-page-modern">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Settings</p>
            <h1 className="sales-title">Carrier Catalog</h1>
            <p className="sales-subtitle">
              Add and manage the platform master list of carriers available throughout the CRM.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link
              className="sales-secondary-btn"
              to="/settings?tab=Data%20%26%20Customization"
              style={{ textDecoration: "none" }}
            >
              ← Back to Data & Customization
            </Link>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={loadCarriers}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`sales-section-card carrier-message-card ${
              error ? "carrier-message-error" : "carrier-message-success"
            }`}
          >
            {error || success}
          </div>
        )}

        <div className="carrier-grid">
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Add Carriers</h2>
              <p>Enter one carrier per line.</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="sales-form-fields">
                <div className="sales-field sales-field-full">
                  <label>Carrier Names</label>
                  <textarea
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={"Humana\nAetna\nMutual of Omaha\nDelta Dental"}
                    rows={8}
                  />
                </div>
              </div>

              <div className="settings-inline-actions carrier-actions">
                <button
                  type="submit"
                  className="sales-primary-btn"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Add Carrier(s)"}
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => {
                    setName("");
                    clearMessages();
                  }}
                  disabled={saving}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Existing Carriers</h2>
              <p>Search, edit, or delete carriers.</p>
            </div>

            <div className="sales-form-fields carrier-search-row">
              <div className="sales-field sales-field-full">
                <label>Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search carriers..."
                />
              </div>
            </div>

            <div className="carrier-table-wrap">
              {filteredCarriers.length > 0 ? (
                <table className="carrier-table">
                  <thead>
                    <tr>
                      <th>Carrier</th>
                      <th className="carrier-table-action-col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCarriers.map((carrier) => (
                      <tr
                        key={carrier.id}
                        onClick={() => {
                          setSelectedCarrier(carrier);
                          setEditedName(carrier.name || "");
                        }}
                      >
                        <td>{carrier.name}</td>
                        <td className="carrier-table-action-col">
                          <button
                            type="button"
                            className="carrier-table-edit-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedCarrier(carrier);
                              setEditedName(carrier.name || "");
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="carrier-empty-state">
                  {loading ? "Loading carriers..." : "No carriers found."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedCarrier && (
        <div
          className="modal-overlay"
          onClick={() => {
            setSelectedCarrier(null);
            setEditedName("");
          }}
        >
          <div className="modal carrier-edit-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Edit Carrier</h3>

            <div className="sales-field sales-field-full">
              <label>Carrier Name</label>
              <input
                type="text"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
              />
            </div>

            <div className="modal-actions carrier-modal-actions">
              <button
                type="button"
                className="modal-button save"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                type="button"
                className="modal-button neutral"
                onClick={() => {
                  setSelectedCarrier(null);
                  setEditedName("");
                }}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-button delete"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Carriers;